import {
  isPlayedResult,
  pointsFor,
  type PointSystem,
  type ResultCode,
  type RoundEntry,
  type Score,
} from '../model/types.js';

/**
 * Tiebreak inputs, following FIDE C.07 (Play-off and Tie-Break Regulations,
 * applied 1 March 2026), Articles 15-16 on unplayed rounds.
 */

/** C.07 16.2 categories of unplayed rounds (0 = a game played over the board). */
export type UnplayedCategory = 0 | 1 | 2 | 3 | 4 | 5;

export interface TbRound {
  readonly round: number;
  readonly opponent: number | null;
  readonly colour: 'w' | 'b' | null;
  readonly result: ResultCode;
  /** Points awarded for the round (tenths). */
  readonly points: Score;
  /** A game played over the board. */
  readonly played: boolean;
  /** Voluntary unplayed round: a requested bye (HPB/ZPB), forfeit loss or absence. */
  readonly vur: boolean;
  readonly category: UnplayedCategory;
}

export interface TbEntrant {
  readonly id: number;
  readonly rating: number;
  readonly rounds: readonly TbRound[];
  /** Actual score over the rounds considered (tenths). */
  readonly score: Score;
  /**
   * Score as seen by opponents (16.3): requested byes (and absences) after the
   * last non-voluntary round count as draws.
   */
  readonly adjusted: Score;
}

export interface TiebreakContext {
  readonly entrants: ReadonlyMap<number, TbEntrant>;
  readonly pointSystem: PointSystem;
  /** Rounds considered (for intermediate standings, the rounds played so far). */
  readonly rounds: number;
  /** Total rounds of the tournament. */
  readonly totalRounds: number;
  /** Round robin or other pre-determined pairings (C.07 15.2). */
  readonly predetermined: boolean;
}

function categorise(history: readonly RoundEntry[], ps: PointSystem): TbRound[] {
  const vurAt = history.map((e) => {
    if (isPlayedResult(e.result) && e.opponent !== null) return false;
    // Unplayed: a win's worth (PAB, FPB, forfeit win) is not voluntary.
    return pointsFor(e.result, ps) < ps.win;
  });
  return history.map((e, i) => {
    const points = pointsFor(e.result, ps);
    const played = isPlayedResult(e.result) && e.opponent !== null;
    const vur = vurAt[i] as boolean;
    let category: UnplayedCategory = 0;
    if (!played) {
      if (e.result === 'U' || e.result === 'F') category = 1;
      else if (e.result === '+' || (e.opponent !== null && points >= ps.win)) category = 2;
      else if (e.opponent !== null) category = 4;
      else {
        // Requested bye or absence: .3 if a non-voluntary round follows.
        const laterNonVur = vurAt.slice(i + 1).some((v) => !v);
        category = laterNonVur ? 3 : 5;
      }
    }
    return {
      round: i + 1,
      opponent: e.opponent,
      colour: e.colour,
      result: e.result,
      points,
      played,
      vur,
      category,
    };
  });
}

export interface TbInputPlayer {
  readonly id: number;
  readonly rating: number;
  readonly history: readonly RoundEntry[];
}

/**
 * Build the tiebreak context. `rounds` is the number of rounds considered;
 * histories shorter than that are padded with absences (zero-point byes).
 */
export function buildContext(
  players: readonly TbInputPlayer[],
  ps: PointSystem,
  rounds: number,
  totalRounds: number,
  predetermined: boolean,
): TiebreakContext {
  const entrants = new Map<number, TbEntrant>();
  for (const p of players) {
    const h: RoundEntry[] = p.history.slice(0, rounds).filter((e) => e.result !== '*');
    while (h.length < rounds) h.push({ opponent: null, colour: null, result: 'Z' });
    const tr = categorise(h, ps);
    let score = 0;
    let adjusted = 0;
    for (const r of tr) {
      score += r.points;
      if (predetermined)
        adjusted += r.opponent === null && !r.played && r.category === 5 ? 0 : r.points;
      else adjusted += r.category === 5 ? ps.draw : r.points;
    }
    entrants.set(p.id, { id: p.id, rating: p.rating, rounds: tr, score, adjusted });
  }
  return { entrants, pointSystem: ps, rounds, totalRounds, predetermined };
}
